import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useYieldCallback } from '@zillow/yield-callback';

import BedButtons from 'app/shared/modules/MobileSRP/Header/FilterModules/BedBathComboFilter/BedButtons';
import Checkbox from 'app/shared/modules/MobileSRP/__BaseComponents__/Checkbox';

import { useFilterChange } from 'app/shared/modules/filter/FilterBar/hooks';
import { shouldShowBedroomApplyButton } from './utils';

import {
  GeneralApplyFilterButton,
  GeneralFilterHeader,
  GeneralFilterLabel,
} from 'app/shared/modules/filter/FilterBar/styles';

type BedState = {
  anyOrStudio: boolean;
  oneOrMore: boolean;
  twoOrMore: boolean;
  threeOrMore: boolean;
  fourOrMore: boolean;
  isExact: boolean;
};

type ReduxState = {
  filter: {
    bedrooms: BedState;
  };
};

/**
 * Reset state used when toggling between bedroom options
 * Ensures only one bedroom option is selected at a time
 */
const FALSY_STATE: BedState = {
  anyOrStudio: false,
  oneOrMore: false,
  twoOrMore: false,
  threeOrMore: false,
  fourOrMore: false,
  isExact: false,
};

/**
 * Bedroom filter selector component for the Info Card on mobile SRP
 *
 * ## Purpose
 * Allows users to filter listings by number of bedrooms with two modes:
 * - **Minimum mode** (default): "2+ bedrooms" shows listings with 2 or more bedrooms
 * - **Exact mode**: "Exactly 2 bedrooms" shows only listings with exactly 2 bedrooms
 *
 * ## Business Context
 * Most users want "at least X bedrooms" (minimum mode), but some users need
 * exact matches (e.g., looking for exactly a 2BR apartment, not 3BR or larger).
 * The "Use exact number of bedrooms" checkbox enables this precision.
 *
 * ## UX Behavior
 * - Apply button only shows when user has made a meaningful selection
 * - Selecting a bedroom option auto-resets other bedroom selections (mutually exclusive)
 * - The exact checkbox can be toggled independently and takes effect on next Apply
 *
 * ## Component State
 * Maintains local state that syncs with Redux only when user clicks Apply.
 * This prevents unwanted filter changes while user is still selecting options.
 *
 * @returns JSX element containing bedroom filter UI
 */
const InfoCardBedSelector = (): JSX.Element => {
  const bedState = useSelector((state: ReduxState) => state.filter.bedrooms);

  const [filterState, setFilterState] = useState<BedState>(bedState);

  const { filterChange } = useFilterChange();

  const shouldShowApplyButton = shouldShowBedroomApplyButton(filterState);

  const yieldTrackFilterChange = useYieldCallback(() => {
    filterChange({ bedrooms: filterState });
  });

  /**
   * Applies the current bedroom filter selection to Redux
   * This triggers a new listing search with the updated filter
   */
  const handleFilterApply = (): void => {
    yieldTrackFilterChange();
  };

  /**
   * Handles clicking a bedroom option button (e.g., "2+", "3+")
   *
   * Implements mutually exclusive selection: clicking a new bedroom count
   * automatically deselects other bedroom counts, preserving only the isExact state.
   */
  const handleBedroomOptionClick = (e: React.MouseEvent<HTMLElement>): void => {
    const option = e.currentTarget.getAttribute('data-option-value');

    if (!option) return;

    // Reset all bedroom selections except isExact, then toggle the clicked option
    const change: BedState = Object.assign({}, FALSY_STATE, { isExact: filterState.isExact });
    change[option as keyof BedState] = !filterState[option as keyof BedState] as boolean;

    setFilterState({
      ...change,
    });
  };

  /**
   * Toggles the "Use exact number of bedrooms" checkbox
   *
   * When enabled, changes filter behavior from "X or more bedrooms" to "exactly X bedrooms".
   * Does not trigger filter change until user clicks Apply.
   */
  const handleBedCheckboxClick = (): void => {
    setFilterState({
      ...filterState,
      isExact: !filterState.isExact,
    });
  };

  return (
    <>
      <GeneralFilterHeader>
        <GeneralFilterLabel id="bedroom-info-card-label">Bedrooms</GeneralFilterLabel>
        {shouldShowApplyButton && (
          <GeneralApplyFilterButton onClick={handleFilterApply} aria-label={'Apply number of bedrooms filter'}>
            Apply
          </GeneralApplyFilterButton>
        )}
      </GeneralFilterHeader>
      <BedButtons handleClick={handleBedroomOptionClick} state={filterState} />
      <Checkbox
        onChange={handleBedCheckboxClick}
        isChecked={filterState.isExact}
        ariaLabel={'Use exact number of bedrooms'}
      >
        Use exact number of bedrooms
      </Checkbox>
    </>
  );
};

export default InfoCardBedSelector;
